import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { PlusOutlined } from "@ant-design/icons";
import { useRequest } from "ahooks";
import { Button, message, Modal, Spin, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import FieldForm, { FieldFormInstance } from "./FieldForm";
import CriteriaItem from "./CriteriaItem";
import { cloneDeep, remove } from "lodash";
import { confirm, omit } from "@/utils";
import {
    addFrequentlyOrderSearchCriteria,
    deleteFrequentlyOrderSearchCriteria,
    updateFrequentlyOrderSearchCriteria,
} from "@/services/api/frequentlyOrderSearchCriteria";
import { useFrequentlyOrderSearchCriteriaStore } from "@/scenes/OrderManagement/store/FrequentlyOrderSearchCriteriaStore";
import { observer } from "mobx-react-lite";
import FrequentlyUsedOrderSearchCriteriaDto from "@/dto/FrequentlyUsedOrderSearchCriteriaDto";
import { useGlobalStore, useOrderMarkStore, useShippingAreaStore } from "@/store/hooks";
import IFrequentlyUsedOrderSearchCriteriaDto from "./type";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDragDropDataTransformer } from "@/hooks/useDragDrop";
import { OrderTagSearchType } from "../OrderTagSelectField";
import AppVersion from "@/enum/AppVersion";
import { showShopPremiumModal } from "@/services/http/utils";
import { PremiumFunctionType } from "@/services/PremiumValidator";
import "./index.less";
import delay from "@/utils/delay";
import { useOrderManagementStore } from "@/scenes/OrderManagementForAllShop/stores/OrderManagementForAllShopStore";

interface FrequentlyUsedOrderSearchCriteriaModalProps extends InjectBaseModalProps {
    entityId: number; // shopId 或 companyId
    entityType: FrequentlyUsedOrderSearchEntity; // 区分来源
}

export const MAX_ORDER_SEARCH_CRITERION = 50;
const FRONT_END_CRITERIA_ID_PREFIX = "__EC_CRITERIA__";

function buildOriginOrderSearchCriteria(index: number, entityId: number, entityType: FrequentlyUsedOrderSearchEntity) {
    const id = FRONT_END_CRITERIA_ID_PREFIX + new Date().getTime();
    const name = `搜索条件${index}`;
    return {
        name,
        id,
        [entityType === FrequentlyUsedOrderSearchEntity.SHOP ? "shopId" : "companyId"]: entityId,
        data: JSON.stringify({ name }),
    };
}

export enum FrequentlyUsedOrderSearchEntity {
    SHOP = "SHOP",
    COMPANY = "COMPANY",
}

/**
 * 管理常用订单搜索条件设置模态框
 *
 * 管理订单搜索条件的加载、添加、删除、更新等操作，并通过表单和拖拽功能实现条件的编辑和排序。同时，根据店铺版本限制常用搜索条件的数量。
 */
const FrequentlyUsedOrderSearchCriteriaModal: React.FC<FrequentlyUsedOrderSearchCriteriaModalProps> = function (props) {
    const { open, onCancel, entityId, entityType } = props;
    const globalStore = useGlobalStore();
    const [criterias, setCriterias] = useState<IFrequentlyUsedOrderSearchCriteriaDto[]>([]);
    const [active, setActive] = useState<number | string>();
    const fieldFormRef = useRef<FieldFormInstance>();
    const {
        loading: loadingStore,
        frequentlyOrderSearchCriterions,
        deleteOrderSearchCriteria,
        updateOrderSearchCriteria,
        handleSort,
    } = useFrequentlyOrderSearchCriteriaStore();
    const orderMarkStore = useOrderMarkStore();
    const shippingAreaStore = useShippingAreaStore();
    const orderManagementStore = useOrderManagementStore();

    /** 将懒加载的选项加载出来 */
    useRequest(orderMarkStore.loadList);
    useRequest(shippingAreaStore.loadList);

    useEffect(() => {
        if (loadingStore) return;

        if (frequentlyOrderSearchCriterions.length) {
            setCriterias(frequentlyOrderSearchCriterions);
            setActive(frequentlyOrderSearchCriterions[0].id);
        } else {
            const origin = buildOriginOrderSearchCriteria(1, entityId, entityType);
            setCriterias([origin]);
            setActive(origin.id);
        }
    }, [loadingStore]);

    function handleAdd() {
        const origin = buildOriginOrderSearchCriteria(criterias.length + 1, entityId, entityType);
        setCriterias((prev) => [...prev, origin]);
        setActive(origin.id);
    }

    async function handleRemove(id: string | number) {
        const newList = [...criterias];

        if (typeof id === "number") {
            await confirm("确认删除该常用条件嘛");
            await deleteFrequentlyOrderSearchCriteria({ id });
            message.success("已删除");
            deleteOrderSearchCriteria(id);
        }

        remove(newList, (item) => item.id === id);
        setCriterias(newList);
        setActive(newList[newList.length - 1]?.id);

        if (!newList.length) fieldFormRef.current?.resetFields();
        if (orderManagementStore.table.activeCriteriaId === id) {
            orderManagementStore.table.setActiveCriteriaId(null);
        }
    }

    const { loading: saving, runAsync: handleSave } = useRequest(
        async () => {
            const index = criterias.findIndex(({ id }) => id === active);
            let res: FrequentlyUsedOrderSearchCriteriaDto;

            // 标准版 限制逻辑（仅 shop 且需要检查时启用）
            const checkPremium = async () => {
                if (entityType !== FrequentlyUsedOrderSearchEntity.SHOP) return false;

                await globalStore.getShopAppVersion(entityId);
                const info = globalStore.shopAppVersionMapping.get(entityId);
                const isPremium = info?.appVersion === AppVersion.PREMIUM;
                if (criterias.length > 1 && !isPremium) {
                    const shopName = globalStore.shopMapping.get(entityId)?.name;
                    showShopPremiumModal(
                        `【${shopName}】店铺是标准版本，最多只能添加和使用1个常用搜索条件，需要使用更多则欢迎【${shopName}】店铺补差价升级后使用`,
                        entityId,
                        PremiumFunctionType.FREQUENTLY_USED_ORDER_SEARCH_CRITERIA,
                    );
                    return true;
                }
                return false;
            };

            const { id, data: savedData, name: savedName, sortedIndex } = criterias[index];
            const isAdd = String(id).startsWith(FRONT_END_CRITERIA_ID_PREFIX);

            if (await checkPremium()) return;

            if (isAdd) {
                res = await addFrequentlyOrderSearchCriteria({
                    [entityType === FrequentlyUsedOrderSearchEntity.SHOP ? "shopId" : "companyId"]: entityId,
                    data: savedData,
                    name: savedName,
                });
            } else {
                // 如果是之前是高级版，但是现在是标准版，并且常用搜索数量大于1，直接限制保存操作
                if (frequentlyOrderSearchCriterions.length > 1) {
                    if (await checkPremium()) return;
                }

                res = await updateFrequentlyOrderSearchCriteria({
                    id: id as number,
                    data: savedData,
                    name: savedName,
                    sortedIndex,
                });
            }

            setActive(res.id);
            message.success(`${res.name}保存成功`);

            const updated = [...criterias];
            if (index === -1) updated.push(res);
            else updated[index] = res;

            setCriterias(updated);
            updateOrderSearchCriteria(res.id, res);
        },
        { manual: true },
    );

    async function handleFieldChange() {
        // 部分子组件有onChange的副作用，这里需要延迟保存表单
        await delay(100);

        const valuesWithName = fieldFormRef.current.getFieldsValue();
        const values = omit(valuesWithName, ["name"]);
        const newList = cloneDeep(criterias);

        for (let i = 0; i < newList.length; i++) {
            if (newList[i].id === active) {
                newList[i].data = JSON.stringify(values);
                newList[i].name = valuesWithName.name;
            }
        }
        setCriterias(newList);
    }

    useEffect(() => {
        if (active && fieldFormRef.current) {
            const activeCriteria = criterias.find(({ id }) => id === active);
            if (!activeCriteria) return;

            let values: any = {};
            try {
                values = JSON.parse(activeCriteria.data);
            } catch {}

            /** 兼容旧快捷搜索字段 */
            if (values.quick) {
                values.quicks = [values.quick];
                delete values.quick;
            }

            /** 兼容旧包含/不包含标签字段 */
            if (values.orderTagSearchType) {
                if (values.orderTagSearchType === OrderTagSearchType.UnInclude) {
                    values.excludedOrderTags = values.orderTags;
                    values.orderTags = undefined;
                }
                delete values.orderTagSearchType;
            }

            fieldFormRef.current.resetFields();
            fieldFormRef.current.setFieldsValue({ ...values, name: activeCriteria.name });
        }
    }, [active]);

    const loading = saving || loadingStore;
    const disabledDrag = criterias?.some((it) => typeof it.id !== "number");
    const { localList, onLocalChange, onLocalDragEnd } = useDragDropDataTransformer(criterias, handleSort);

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            width={980}
            okText="保存"
            onOk={handleSave}
            okButtonProps={{ loading }}
            title={
                <span>
                    常用搜索条件设置<span className="small text-light">（最多设置50个搜索条件）</span>
                </span>
            }
        >
            <Spin spinning={loading}>
                <div className="search-setting-container">
                    <div>
                        <Tooltip title="最多只支持50个常用搜索条件">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={criterias.length === MAX_ORDER_SEARCH_CRITERION}
                                onClick={handleAdd}
                            >
                                添加搜索条件
                            </Button>
                        </Tooltip>
                        <div className="criteria-list">
                            <DndProvider backend={HTML5Backend}>
                                {localList.map((criteria, index) => (
                                    <CriteriaItem
                                        key={criteria.id}
                                        index={index}
                                        orderSearchCriteria={criteria}
                                        active={active}
                                        onActive={setActive}
                                        onRemove={handleRemove}
                                        onSortChange={onLocalChange}
                                        onSortChangeEnd={onLocalDragEnd}
                                        disabledDrag={disabledDrag}
                                    />
                                ))}
                            </DndProvider>
                        </div>
                    </div>

                    <FieldForm
                        ref={fieldFormRef}
                        onFieldsChange={handleFieldChange}
                        shopId={entityType === FrequentlyUsedOrderSearchEntity.SHOP ? entityId : null}
                    />
                </div>
            </Spin>
        </Modal>
    );
};

export default observer(FrequentlyUsedOrderSearchCriteriaModal);
